import { useCallback, useState } from "react";

import { Button } from "V2.0/common/button";

import useConversationReminderState, {
  ConversationReminderState,
} from "./useConversationReminderState";

import ConversationReminderForm from "./ConversationReminderForm";

import { ConversationReminderMessageType } from "./types";

import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { updateConversationReminderSettings } from "../../service";

export default ConversationReminder;

const defaultConversationReminder = {
  enabled: false,
  delayInMinutes: 60,
  messageType: ConversationReminderMessageType.Static,
  staticMessage: "Just checking in! Do you have any other questions?",
  dynamicMessage:
    "The user has not replied to my last message. Generate a brief, friendly follow-up message to re-engage them.",
};

function ConversationReminder(props) {
  const { messagingSettings, syncAssistant, workspaceId, assistantId, locked } =
    props;

  const [loading, setLoading] = useState<boolean>(false);

  const handleSave = useCallback(async (data: ConversationReminderState) => {
    const { errors, ...restData } = data;

    const payload = restData;

    const lifecycleCbs = {
      init() {
        setLoading(true);
        message.loading("Saving Converstaion Reminders");
      },
      success() {
        setLoading(false);
        message.destroy();
        message.success("Conversation Reminders Saved");
      },
      error(e) {
        setLoading(false);
        message.destroy();
        showErrorToast(e, "Failed to save Conversation Reminders");
      },
    };

    lifecycleCbs.init();

    try {
      const response = await updateConversationReminderSettings({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        lifecycleCbs.success();

        syncAssistant((prevAssistant) => {
          const updatedMessagingSettings = {
            ...prevAssistant.messagingSettings,
            conversationReminder: response.data.settings,
          };

          return {
            ...prevAssistant,
            messagingSettings: updatedMessagingSettings,
          };
        });
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }, []);

  const conversationReminder =
    messagingSettings?.conversationReminder ?? defaultConversationReminder;

  const conversationReminderState =
    useConversationReminderState(conversationReminder);

  const renderHeader = (
    <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
      <p className="text-md font-semibold text-gray-900">
        Conversation Reminders
      </p>
      <p className="text-sm mt-1 text-gray-500">
        Send reminder messages to follow up on inactive conversations.
      </p>
    </div>
  );

  const renderBody = (
    <ConversationReminderForm
      {...conversationReminderState}
      locked={locked}
    />
  );

  const renderFooter = (
    <div className="bg-gray-50 px-6 py-3 text-right rounded-b-lg">
      <Button
        loading={loading}
        onClick={() => handleSave(conversationReminderState.state)}
        disabled={loading || conversationReminderState.isError}
      >
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col w-3/5 min-w-[500px] gap-y-2 border rounded-lg m-4">
      {renderHeader}
      {renderBody}
      {!locked && renderFooter}
    </div>
  );
}
